import { Injectable } from "@angular/core"
import { Subject, BehaviorSubject ,Observable,of} from "rxjs"
import { share, tap } from "rxjs/operators"
import { FormGroup } from '@angular/forms';

@Injectable({
  providedIn: "root",
})
export class SharedServicesHeader {
  private emitChangeSource = new Subject<any>();
  changeEmitted$ = this.emitChangeSource.asObservable();
  emitChange(change: any) {
    this.emitChangeSource.next(change);
  }
}

@Injectable({
  providedIn: 'root'
})
export class SharedServicesService {
  private emitChangeSource = new Subject<any>();
  changeEmitted$ = this.emitChangeSource.asObservable();
  private eventDetailsSubject = new BehaviorSubject<any>(null);
  public eventDetails$ = this.eventDetailsSubject.asObservable();

  private metadataSubject = new BehaviorSubject<any>(null);
  public metadata$ = this.metadataSubject.asObservable();
  private metadataLoadingSubject = new BehaviorSubject<boolean>(false);
  public metadataLoading$ = this.metadataLoadingSubject.asObservable();
  private metadataErrorSubject = new BehaviorSubject<string | null>(null);
  public metadataError$ = this.metadataErrorSubject.asObservable();
  private metadataRequest: Observable<any> | null = null;

  private maintenanceModeSubject = new BehaviorSubject<boolean>(false);
  public maintenanceMode$ = this.maintenanceModeSubject.asObservable();

  private currentEventId: string | null = null;
  private currentEventDetails: any = null;
  private pendingRequests: Map<string, Observable<any>> = new Map();
  private requestInitiationFlags: Map<string, boolean> = new Map();
  private eventDetailsCache: { [eventId: string]: any } = {};

  private formCache: { [key: string]: any } = {};
  private formDetailsCache: { [key: string]: any } = {};
  private formPendingRequests: Map<string, Observable<any>> = new Map();
  private formDetailsPendingRequests: Map<string, Observable<any>> = new Map();
  private formRequestInitiationFlags: Map<string, boolean> = new Map();
  private formDetailsRequestInitiationFlags: Map<string, boolean> = new Map();
  private commonLookupCache: { [key: string]: any } = {};
  private commonLookupLoaded: boolean = false;

  emitChange(change: any) {
    this.emitChangeSource.next(change);
  }

  // Metadata methods
  setMetadata(metadata: any) {
    this.metadataSubject.next(metadata);
    this.metadataLoadingSubject.next(false);
    this.metadataErrorSubject.next(null);
    this.metadataRequest = null;
    
    const isMaintenanceMode = metadata?.maintenance_mode === 'true' || metadata?.maintenance_mode === true;
    this.maintenanceModeSubject.next(isMaintenanceMode);
  }

  setMetadataError(error: string, statusCode?: number) {
    if (statusCode === 502 || statusCode === 503) {
      this.maintenanceModeSubject.next(true);
    } else {
      this.metadataErrorSubject.next(error);
    }
    this.metadataLoadingSubject.next(false);
    this.metadataRequest = null;
  }

  setMetadataLoading(loading: boolean) {
    this.metadataLoadingSubject.next(loading);
  }

  getMetadata(): any {
    return this.metadataSubject.value;
  }

  isMetadataLoaded(): boolean {
    return this.metadataSubject.value !== null;
  }

  initiateMetadataRequest(requestFactory: () => Observable<any>): Observable<any> {
    if (this.isMetadataLoaded()) {
      return of(this.getMetadata());
    }

    if (this.metadataRequest) {
      return this.metadataRequest;
    }

    this.setMetadataLoading(true);
    this.metadataRequest = requestFactory().pipe(
      tap({
        next: (response: any) => {
          if (response.body?.company_details) {
            this.setMetadata(response.body.company_details);
          }
        },
        error: (error: any) => {
          const statusCode = error.status;
          this.setMetadataError(error.error?.detail || 'Failed to fetch company details', statusCode);
        }
      }),
      share()
    );

    return this.metadataRequest;
  }

  clearMetadata() {
    this.metadataSubject.next(null);
    this.metadataLoadingSubject.next(false);
    this.metadataErrorSubject.next(null);
    this.metadataRequest = null;
    this.maintenanceModeSubject.next(false);
  }

  isMaintenanceMode(): boolean {
    return this.maintenanceModeSubject.value;
  }

  getMaintenanceMode(): boolean {
    return this.maintenanceModeSubject.value;
  }

  setEventDetails(eventId: string, eventDetails: any) {
    this.currentEventId = eventId;
    this.currentEventDetails = eventDetails;
    this.eventDetailsCache[eventId] = eventDetails;
    this.eventDetailsSubject.next(eventDetails);
    this.pendingRequests.delete(eventId);
    this.requestInitiationFlags.delete(eventId);
  }

  getEventDetails(eventId: string): any {
    if (this.eventDetailsCache[eventId]) {
      return this.eventDetailsCache[eventId];
    }
    if (this.currentEventId === eventId) {
      return this.currentEventDetails;
    }
    return null;
  }
  isEventDetailsLoaded(eventId: string): boolean {
    return !!this.eventDetailsCache[eventId];
  }
  
  clearEventDetails(eventId: string) {
    delete this.eventDetailsCache[eventId];
    if (this.currentEventId === eventId) {
      this.currentEventId = null;
      this.currentEventDetails = null;
      this.eventDetailsSubject.next(null);
    }
    this.pendingRequests.delete(eventId);
    this.requestInitiationFlags.delete(eventId);
  }
  hasPendingRequest(eventId: string): boolean {
    return this.pendingRequests.has(eventId);
  }
  isRequestBeingInitiated(eventId: string): boolean {
    return this.requestInitiationFlags.get(eventId) === true;
  }
  initiateEventDetailsRequest(eventId: string, requestFactory: () => Observable<any>): Observable<any> | null {
    if (this.isEventDetailsLoaded(eventId)) {
      return of(this.getEventDetails(eventId));
    }
    if (this.pendingRequests.has(eventId)) {
      return this.pendingRequests.get(eventId)!;
    }

    if (this.requestInitiationFlags.get(eventId) === true) {
      return new Observable(subscriber => {
        const checkInterval = setInterval(() => {
          if (this.pendingRequests.has(eventId)) {
            clearInterval(checkInterval);
            const pendingRequest = this.pendingRequests.get(eventId)!;
            pendingRequest.subscribe(subscriber);
          } else if (this.isEventDetailsLoaded(eventId)) {
            clearInterval(checkInterval);
            subscriber.next({ body: { event_details: this.getEventDetails(eventId) } });
            subscriber.complete();
          }
        }, 10);
        
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!subscriber.closed) {
            subscriber.error(new Error('Request initiation timeout'));
          }
        }, 5000);
      });
    }

    this.requestInitiationFlags.set(eventId, true);
    const request = requestFactory();
    const sharedRequest = request.pipe(
      tap({
        next: () => {
          this.pendingRequests.delete(eventId);
          this.requestInitiationFlags.delete(eventId);
        },
        error: () => {
          this.pendingRequests.delete(eventId);
          this.requestInitiationFlags.delete(eventId);
        }
      }),
      share()
    );

    this.pendingRequests.set(eventId, sharedRequest);
    return sharedRequest;
  }
  setPendingRequest(eventId: string, request: Observable<any>): Observable<any> {
    const sharedRequest = request.pipe(
      tap(() => {
        this.pendingRequests.delete(eventId);
      }),
      share()
    );
    
    this.pendingRequests.set(eventId, sharedRequest);
    return sharedRequest;
  }
  getPendingRequest(eventId: string): Observable<any> | null {
    return this.pendingRequests.get(eventId) || null;
  }
  resetEventDetails() {
    this.currentEventId = null;
    this.currentEventDetails = null;
    this.eventDetailsSubject.next(null);
    this.pendingRequests.clear();
  }

  getCurrentEventId(): string | null {
    return this.currentEventId;
  }

  // Form caching methods
  setFormData(eventId: string, formData: any) {
    const cacheKey = `form_${eventId}`;
    this.formCache[cacheKey] = formData;
    this.formPendingRequests.delete(cacheKey);
    this.formRequestInitiationFlags.delete(cacheKey);
  }

  getFormData(eventId: string): any {
    const cacheKey = `form_${eventId}`;
    return this.formCache[cacheKey] || null;
  }

  isFormDataLoaded(eventId: string): boolean {
    const cacheKey = `form_${eventId}`;
    return !!this.formCache[cacheKey];
  }

  clearFormData(eventId: string) {
    const cacheKey = `form_${eventId}`;
    delete this.formCache[cacheKey];
    this.formPendingRequests.delete(cacheKey);
    this.formRequestInitiationFlags.delete(cacheKey);
  }

  hasFormPendingRequest(eventId: string): boolean {
    const cacheKey = `form_${eventId}`;
    return this.formPendingRequests.has(cacheKey);
  }

  isFormRequestBeingInitiated(eventId: string): boolean {
    const cacheKey = `form_${eventId}`;
    return this.formRequestInitiationFlags.get(cacheKey) === true;
  }

  initiateFormRequest(eventId: string, requestFactory: () => Observable<any>): Observable<any> | null {
    const cacheKey = `form_${eventId}`;
    
    if (this.isFormDataLoaded(eventId)) {
      return of(this.getFormData(eventId));
    }
    
    if (this.formPendingRequests.has(cacheKey)) {
      return this.formPendingRequests.get(cacheKey)!;
    }

    if (this.formRequestInitiationFlags.get(cacheKey) === true) {
      return new Observable(subscriber => {
        const checkInterval = setInterval(() => {
          if (this.formPendingRequests.has(cacheKey)) {
            clearInterval(checkInterval);
            const pendingRequest = this.formPendingRequests.get(cacheKey)!;
            pendingRequest.subscribe(subscriber);
          } else if (this.isFormDataLoaded(eventId)) {
            clearInterval(checkInterval);
            subscriber.next(this.getFormData(eventId));
            subscriber.complete();
          }
        }, 10);
        
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!subscriber.closed) {
            subscriber.error(new Error('Form request initiation timeout'));
          }
        }, 5000);
      });
    }

    this.formRequestInitiationFlags.set(cacheKey, true);
    const request = requestFactory();
    const sharedRequest = request.pipe(
      tap({
        next: (response) => {
          this.setFormData(eventId, response);
          this.formPendingRequests.delete(cacheKey);
          this.formRequestInitiationFlags.delete(cacheKey);
        },
        error: () => {
          this.formPendingRequests.delete(cacheKey);
          this.formRequestInitiationFlags.delete(cacheKey);
        }
      }),
      share()
    );

    this.formPendingRequests.set(cacheKey, sharedRequest);
    return sharedRequest;
  }

  // Form details caching methods
  setFormDetailsData(eventId: string, formDetailsData: any) {
    const cacheKey = `form_detail_${eventId}`;
    this.formDetailsCache[cacheKey] = formDetailsData;
    this.formDetailsPendingRequests.delete(cacheKey);
    this.formDetailsRequestInitiationFlags.delete(cacheKey);
  }

  getFormDetailsData(eventId: string): any {
    const cacheKey = `form_detail_${eventId}`;
    return this.formDetailsCache[cacheKey] || null;
  }

  isFormDetailsDataLoaded(eventId: string): boolean {
    const cacheKey = `form_detail_${eventId}`;
    return !!this.formDetailsCache[cacheKey];
  }

  clearFormDetailsData(eventId: string) {
    const cacheKey = `form_detail_${eventId}`;
    delete this.formDetailsCache[cacheKey];
    this.formDetailsPendingRequests.delete(cacheKey);
    this.formDetailsRequestInitiationFlags.delete(cacheKey);
  }

  hasFormDetailsPendingRequest(eventId: string): boolean {
    const cacheKey = `form_detail_${eventId}`;
    return this.formDetailsPendingRequests.has(cacheKey);
  }

  isFormDetailsRequestBeingInitiated(eventId: string): boolean {
    const cacheKey = `form_detail_${eventId}`;
    return this.formDetailsRequestInitiationFlags.get(cacheKey) === true;
  }

  initiateFormDetailsRequest(eventId: string, requestFactory: () => Observable<any>): Observable<any> | null {
    const cacheKey = `form_detail_${eventId}`;
    
    if (this.isFormDetailsDataLoaded(eventId)) {
      return of(this.getFormDetailsData(eventId));
    }
    
    if (this.formDetailsPendingRequests.has(cacheKey)) {
      return this.formDetailsPendingRequests.get(cacheKey)!;
    }

    if (this.formDetailsRequestInitiationFlags.get(cacheKey) === true) {
      return new Observable(subscriber => {
        const checkInterval = setInterval(() => {
          if (this.formDetailsPendingRequests.has(cacheKey)) {
            clearInterval(checkInterval);
            const pendingRequest = this.formDetailsPendingRequests.get(cacheKey)!;
            pendingRequest.subscribe(subscriber);
          } else if (this.isFormDetailsDataLoaded(eventId)) {
            clearInterval(checkInterval);
            subscriber.next(this.getFormDetailsData(eventId));
            subscriber.complete();
          }
        }, 10);
        
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!subscriber.closed) {
            subscriber.error(new Error('Form details request initiation timeout'));
          }
        }, 5000);
      });
    }

    this.formDetailsRequestInitiationFlags.set(cacheKey, true);
    const request = requestFactory();
    const sharedRequest = request.pipe(
      tap({
        next: (response) => {
          this.setFormDetailsData(eventId, response);
          this.formDetailsPendingRequests.delete(cacheKey);
          this.formDetailsRequestInitiationFlags.delete(cacheKey);
        },
        error: () => {
          this.formDetailsPendingRequests.delete(cacheKey);
          this.formDetailsRequestInitiationFlags.delete(cacheKey);
        }
      }),
      share()
    );

    this.formDetailsPendingRequests.set(cacheKey, sharedRequest);
    return sharedRequest;
  }

  // Clear all form caches
  clearAllFormCaches(eventId: string) {
    this.clearFormData(eventId);
    this.clearFormDetailsData(eventId);
  }

  // Reset all caches
  resetAllCaches() {
    this.resetEventDetails();
    this.formCache = {};
    this.formDetailsCache = {};
    this.formPendingRequests.clear();
    this.formDetailsPendingRequests.clear();
    this.formRequestInitiationFlags.clear();
    this.formDetailsRequestInitiationFlags.clear();
    this.commonLookupCache = {};
    this.commonLookupLoaded = false;
  }
  setCommonLookupData(data: any) {
    this.commonLookupCache = { ...data };
    this.commonLookupLoaded = true;
  }

  getCommonLookupData(): any {
    return this.commonLookupLoaded ? this.commonLookupCache : null;
  }

  isCommonLookupDataLoaded(): boolean {
    return this.commonLookupLoaded;
  }

  clearCommonLookupData() {
    this.commonLookupCache = {};
    this.commonLookupLoaded = false;
  }
  hasSecurityViolations(form: FormGroup, fieldsToCheck?: string[]): boolean {
    if (!form) return false;
    const fieldNames = fieldsToCheck || Object.keys(form.controls);
    
    const dangerousPatterns = [
      /<[^>]*>/,                    // HTML tags
      /javascript:/gi,              // JavaScript protocol
      /on\w+\s*=/gi,               // Event handlers
      /^\s*[\=\+\-@]/,             // CSV formula injection (with leading spaces)
      /cmd\s*\|/gi,                // Command injection
      /powershell/gi,              // PowerShell
      /\<script\>/gi,              // Script tags
      /\<\/script\>/gi,            // Closing script tags
      /expression\s*\(/gi,         // CSS expressions
      /HYPERLINK\s*\(/gi,          // Excel HYPERLINK function
      /SUM\s*\(/gi,                // Excel SUM function
      /CONCATENATE\s*\(/gi,        // Excel CONCATENATE function
      /IF\s*\(/gi,                 // Excel IF function
      /INDIRECT\s*\(/gi,           // Excel INDIRECT function
      /EXEC\s*\(/gi               // Excel EXEC function
    ];

    for (let fieldName of fieldNames) {
      const control = form.get(fieldName);
      if (control?.value) {
        const input = control.value.toString();
        
        if (dangerousPatterns.some(pattern => pattern.test(input))) {
          console.warn(`Security violation detected in field: ${fieldName}`, input.substring(0, 50));
          return true;
        }
      }
    }
    return false;
  }
  getSecurePayload(formValue: any): any {
    if (!formValue) return {};

    const sanitize = (input: any): string => {
      if (!input) return '';
      
      let str = input.toString()
                     .replace(/<[^>]*>/g, '')         // Remove HTML tags
                     .replace(/javascript:/gi, '')    // Remove javascript:
                     .replace(/on\w+\s*=/gi, '');     // Remove event handlers

      const trimmedStr = str.trimStart();
      if (/^[\=\+\-@]/.test(trimmedStr)) {
        const leadingSpaces = str.match(/^\s*/)?.[0] || '';
        const restOfString = str.substring(leadingSpaces.length);
        str = leadingSpaces + "'" + restOfString;
      }
      str = str.replace(/HYPERLINK\s*\(/gi, "'HYPERLINK(")
               .replace(/SUM\s*\(/gi, "'SUM(")
               .replace(/CONCATENATE\s*\(/gi, "'CONCATENATE(")
               .replace(/IF\s*\(/gi, "'IF(")
               .replace(/INDIRECT\s*\(/gi, "'INDIRECT(")
               .replace(/EXEC\s*\(/gi, "'EXEC(")
               .replace(/cmd\s*\|/gi, '')           // Remove command injection
               .replace(/powershell/gi, '')         // Remove PowerShell
               .trimEnd();                          // Only trim end spaces

      return str;
    };

    const securePayload: any = {};    
    Object.keys(formValue).forEach(key => {
      const value = formValue[key];
      
      if (typeof value === 'string') {
        securePayload[key] = sanitize(value);
      } else {
        securePayload[key] = value;
      }
    });

    return securePayload;
  }

  hasSingleInputViolation(input: string): boolean {
    if (!input) return false;

    const dangerousPatterns = [
      /<[^>]*>/,                    // HTML tags
      /javascript:/gi,              // JavaScript protocol
      /on\w+\s*=/gi,               // Event handlers
      /^\s*[\=\+\-@]/,             // CSV formula injection (with leading spaces)
      /cmd\s*\|/gi,                // Command injection
      /powershell/gi,              // PowerShell
      /\<script\>/gi,              // Script tags
      /expression\s*\(/gi,         // CSS expressions
      /HYPERLINK\s*\(/gi,          // Excel HYPERLINK function
      /SUM\s*\(/gi,                // Excel SUM function
      /CONCATENATE\s*\(/gi,        // Excel CONCATENATE function
      /IF\s*\(/gi,                 // Excel IF function
      /INDIRECT\s*\(/gi,           // Excel INDIRECT function
      /EXEC\s*\(/gi               // Excel EXEC function
    ];
    
    return dangerousPatterns.some(pattern => pattern.test(input.toString()));
  }
  sanitizeInput(input: string): string {
    if (!input) return '';
    
    let str = input.toString()
                   .replace(/<[^>]*>/g, '')         // Remove HTML tags
                   .replace(/javascript:/gi, '')    // Remove javascript:
                   .replace(/on\w+\s*=/gi, '');     // Remove event handlers

    const trimmedStr = str.trimStart();
    if (/^[\=\+\-@]/.test(trimmedStr)) {
      const leadingSpaces = str.match(/^\s*/)?.[0] || '';
      const restOfString = str.substring(leadingSpaces.length);
      str = leadingSpaces + "'" + restOfString;
    }
    str = str.replace(/HYPERLINK\s*\(/gi, "'HYPERLINK(")
             .replace(/SUM\s*\(/gi, "'SUM(")
             .replace(/CONCATENATE\s*\(/gi, "'CONCATENATE(")
             .replace(/IF\s*\(/gi, "'IF(")
             .replace(/INDIRECT\s*\(/gi, "'INDIRECT(")
             .replace(/EXEC\s*\(/gi, "'EXEC(")
             .replace(/cmd\s*\|/gi, '')           // Remove command injection
             .replace(/powershell/gi, '')         // Remove PowerShell
             .trimEnd();                          // Only trim end spaces

    return str;
  }

  sanitizeForCsv(input: string): string {
    if (!input) return '';
    let str = input.toString();    
    const trimmedStr = str.trimStart();
    if (/^[\=\+\-@]/.test(trimmedStr)) {
      const leadingSpaces = str.match(/^\s*/)?.[0] || '';
      const restOfString = str.substring(leadingSpaces.length);
      str = leadingSpaces + "'" + restOfString;
    }
    const dangerousFunctions = [
      'HYPERLINK', 'SUM', 'CONCATENATE', 'IF', 'INDIRECT', 'EXEC',
      'CHAR', 'CODE', 'FORMULA', 'IMPORTXML', 'IMPORTHTML', 'IMPORTDATA',
      'GOOGLETRANSLATE', 'WEBSERVICE', 'FILTERXML', 'DOCUMENT'
    ];

    dangerousFunctions.forEach(func => {
      const regex = new RegExp(`\\b${func}\\s*\\(`, 'gi');
      str = str.replace(regex, `'${func}(`);
    });
    str = str.replace(/<[^>]*>/g, '')                // Remove HTML tags
             .replace(/javascript:/gi, '')           // Remove javascript:
             .replace(/on\w+\s*=/gi, '')            // Remove event handlers
             .replace(/cmd\s*\|/gi, '')             // Remove command injection
             .replace(/powershell/gi, '');          // Remove PowerShell

    return str;
  }
  sanitizeCsvData(data: any[][]): any[][] {
    return data.map(row => 
      row.map(cell => 
        typeof cell === 'string' ? this.sanitizeForCsv(cell) : cell
      )
    );
  }

}